import React, { useEffect, useRef, useState, useMemo } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useSearchParams } from "react-router-dom";
import dayjs, { Dayjs } from "dayjs";
import mabAd2 from "../../assets/images/svg/mab-ad2-1.svg";
import SubscriptionProtectedPage from "../../components/subscription/SubscriptionProtectedPage";

import mabAdArrow from "../../assets/images/svg/mab-ad-arrow.svg";
import stepOneIcon from "../../assets/images/svg/mab-1.svg";
import stepTwoIcon from "../../assets/images/svg/mab2.svg";
import stepTwoActiveIcon from "../../assets/images/svg/mab-ad2.1.svg";
import downArrow from "../../assets/images/svg/mab-down-arrow.svg";
import ellipseBg from "../../assets/images/svg/mab-ellipse.svg";
import lineSvg from "../../assets/images/svg/mab-line-2.svg";
import line1Svg from "../../assets/images/svg/line1svg.svg";
import MobileAdPreview from "./meta-mobile-ad-preview";
import AudioDropdown from "../../components/audio-dropdown";
import VideoDropdown from "../../components/metaBuilder/MetaBuilderVideoDropdown";
import LandingDropdown from "../../components/landing-dropdown";
import InterestsDropdown from "../../components/metaBuilder/MetaBuilderInterestsDropdown";
import BudgetDropdown from "../../components/metaBuilder/MetaBuilderBudgetDropdown";
import AdvancedSettingsDropdown from "../../components/metaBuilder/MetaBuilderAdvancedSettingsDropdown";
import ConfirmationDropdown from "../../components/metaBuilder/MetaBuilderConfirmationDropdown";
import MusicDropdown from "../../components/metaBuilder/MetaBuilderMusicDropdown";
import TrackGenreDropdown from "../../components/track-genre-dropdown";
import CountriesDropdown from "../../components/metaBuilder/MetaBuilderCountriesDropdown";
import {
  useGetArtistListQuery,
  useLazyCheckBannedArtistQuery,
} from "../../components/apis/artistsApi";
import {
  useGetMetaBuilderCampaignFromMetaQuery,
  useGetVideosByIdsQuery,
} from "../../components/apis/metaBuilderApi";
import { ROUTES } from "../../components/consts/routes.consts";
import { apiBaseUrl } from "../../components/consts/api-url.const";
import NextButton from "../../components/common/next-button";
import previewButtonIcon from "../../assets/images/svg/preview-button-icon.svg";

const sanitizeAdAccountName = (value?: string | null): string => {
  if (!value) return "";
  return value
    .replace(/\s*\[(?:act_[^\]]+|\d+)\]/g, "")
    .replace(/^\s*(?:act_)?\d+\s+/, "")
    .trim();
};

const formatAdAccountDisplayName = (
  name?: string | null,
  adAccountId?: string | null,
): string | null => {
  const cleanedName = sanitizeAdAccountName(name);
  if (!cleanedName) return adAccountId || null;
  return adAccountId ? `${cleanedName} [${adAccountId}]` : cleanedName;
};

const AdNameStepBar: React.FC<{ showSecondSection: boolean }> = ({
  showSecondSection,
}) => (
  <div className="hidden md:block w-full max-w-[1308px] mx-auto mb-4">
    <div className="flex items-center gap-4 px-4 sm:px-8 lg:px-[60px] py-1 h-[90px] bg-[#0F0F11] rounded-[40px]">
      <p className="text-white text-[18px] leading-[130%] tracking-[0] font-medium">
        Set up the ad account,
        <br />
        Pixel, and campaign name
      </p>

      <div className="flex items-center flex-1">
        <img src={stepOneIcon} alt="Step 1" className="w-[84px] h-[84px]" />
        <div className="flex-1 flex items-center justify-center ml-[-15px]">
          <img
            src={showSecondSection ? lineSvg : line1Svg}
            alt="Line"
            className="w-full h-[2px] mt-[-5px]"
          />
        </div>
        <div className="flex items-center gap-1 ml-[-15px]">
          <img
            src={showSecondSection ? stepTwoActiveIcon : stepTwoIcon}
            alt="Step 2"
            className="w-[84px] h-[84px]"
          />
          <span className="text-white text-[18px] leading-[130%] tracking-[0] font-medium text-right whitespace-nowrap">
            Campaign Set Up
          </span>
        </div>
      </div>
    </div>
  </div>
);

const AdNameNavbar: React.FC<{ onBack?: () => void }> = ({ onBack }) => {
  const navigate = useNavigate();

  const handleBackClick = () => {
    if (onBack) {
      onBack();
      return;
    }
    navigate(ROUTES.META_ADS_SETUP_PUBLIC);
  };

  return (
    <div className="w-full px-4 sm:px-8 lg:px-[60px] mb-4">
      <div className="flex items-center gap-10 ml-[-11px] lg:ml-[-53px]">
        <button
          onClick={handleBackClick}
          className="inline-flex items-center gap-[10px] w-[116px] sm:w-[124px] h-[46px] sm:h-[48px] px-[12px] pr-[24px] sm:pr-[32px] py-[10px] sm:py-[12px] rounded-[999px] bg-white text-[#0B0B0B] shadow-[0_4px_12px_rgba(0,0,0,0.25)] hover:translate-y-[-1px] transition-transform"
          style={{ opacity: 1 }}
        >
          <img
            src={mabAdArrow}
            alt="Back"
            className="w-[25px] h-[25px] rotate-180"
          />
          <span className="text-[16px] font-semibold ">Back</span>
        </button>
      </div>
    </div>
  );
};
// meta new compaing ad name section
const AdNameSection: React.FC<{
  onNext: () => void;
  adName: string;
  onAdNameChange: (v: string) => void;
  promoteType: "track" | "playlist";
  onPromoteTypeChange: (value: "track" | "playlist") => void;
  isEditMode?: boolean;
}> = ({
  onNext,
  adName,
  onAdNameChange,
  promoteType,
  onPromoteTypeChange,
  isEditMode = false,
}) => {
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Limit to 35 characters
    if (value.length <= 35 && !isEditMode) {
      onAdNameChange(value);
    }
  };

  // Check if input is empty or contains only whitespace
  const isDisabled = !adName || !adName.trim();

  return (
    <div
      className="relative z-[2] w-full  rounded-[28px] md:rounded-[40px]   p-[2px]"
      style={{
        background:
          "linear-gradient(180deg, #58F2A9 0%, #1B76FF 79.33%, rgba(255, 170, 45, 0.1) 95.19%)",
        boxShadow: "0px 10px 40px 0px #060C1180",
      }}
    >
      <section
        className="relative w-full box-border h-auto md:min-h-[358px] rounded-[26px] md:rounded-[38px] bg-[#0e0e10]
  px-4 pt-[30px] pb-[80px]
  lg:px-[60px]
  flex flex-col gap-3 md:gap-6 opacity-100 overflow-hidden"
      >
        {/* Centered Top Gradient - responsive size (same as meta-ads-submitting-modal) */}
        <div
          className="absolute left-1/2 -translate-x-1/2 
               w-[280px] h-[90px]
               sm:w-[360px] sm:h-[110px]
               md:w-[460px] md:h-[140px]
               blur-2xl sm:blur-3xl
               pointer-events-none"
          style={{
            top: "-50px",
            background:
              "linear-gradient(226.45deg, #1B76FF 12.8%, #2BFAA3 93.65%)",
          }}
        />
        {/* Centered Bottom Gradient - same style as top */}
        <div
          className="absolute left-1/2 -translate-x-1/2 
               w-[300px] h-[50px]
               sm:w-[360px] 
               md:w-[500px] md:h-[50px]
               blur-2xl sm:blur-3xl
               pointer-events-none"
          style={{
            bottom: "-50px",
            background: "linear-gradient(90deg, #FFAA2D 0%, #581D00 100%)",
          }}
        />

        <div className="relative z-10 flex flex-col gap-3 md:gap-6 w-full max-w-full overflow-hidden h-full">
          {/* What are you looking to promote?
              Only asked when starting a campaign: a live campaign already runs
              against a track or a playlist, and switching afterwards isn't
              supported, so edit mode keeps the type it was created with. */}
          {!isEditMode && (
            <>
              <h2 className="text-white font-normal text-[18px] mt-[50px] leading-[100%] tracking-[0] text-left">
                What are you looking to promote?
              </h2>

              {/* Segmented Control */}
              <div className="inline-flex w-full h-[62px] items-center p-[6px] rounded-[999px] bg-[#252A33]">
                <button
                  type="button"
                  onClick={() => onPromoteTypeChange("track")}
                  className={`flex-1 inline-flex items-center justify-center py-2.5 md:py-3.5 px-3 h-[49px] md:px-5 rounded-[999px] font-medium text-[16px] leading-[1.2] transition-colors whitespace-nowrap ${
                    promoteType === "track"
                      ? "bg-white text-black"
                      : "bg-transparent text-white"
                  }`}
                >
                  Spotify track
                </button>
                <button
                  type="button"
                  onClick={() => onPromoteTypeChange("playlist")}
                  className={`flex-1 inline-flex items-center justify-center h-[49px] py-2.5 md:py-3.5 px-3 md:px-5 rounded-[999px] font-medium text-[16px] leading-[1.2] transition-colors whitespace-nowrap ${
                    promoteType === "playlist"
                      ? "bg-white text-black"
                      : "bg-transparent text-white"
                  }`}
                >
                  Spotify playlist
                </button>
              </div>
            </>
          )}

          {/* Ad Name */}
          <h2
            className={`text-white font-bold lg:text-[32px] text-[24px] leading-[100%] tracking-[0] ${
              isEditMode ? "mt-[50px]" : ""
            }`}
          >
            Ad Name
          </h2>

          {/* Input - placeholder and text vertically centered */}
          <input
            type="text"
            value={adName}
            onChange={handleInputChange}
            maxLength={35}
            placeholder="Enter your ad campaign name"
            disabled={isEditMode}
            className="w-full h-[50px] md:h-[69px] leading-5 py-[15px] md:py-[24px] md:rounded-[20px] rounded-[12px] bg-[#878DA14D] border-2 border-transparent text-white text-[16px] px-4 md:px-6 focus:outline-none focus:border-[#58F2A9] placeholder:text-[#878DA1] box-border"
            style={{
              opacity: isEditMode ? 0.6 : 1,
              cursor: isEditMode ? "not-allowed" : "text",
            }}
          />

          {/* Next Button */}
          <div className="flex w-full justify-center md:justify-end">
            <NextButton
              size="sm"
              onClick={onNext}
              disabled={isDisabled}
              className="w-full md:w-auto"
            />
          </div>
        </div>
      </section>
    </div>
  );
};

export default function CreateCampaign() {
  // Parse campaign ID and draft ID from URL query params for edit mode
  const [searchParams] = useSearchParams();
  const promoteTypeFromQuery = searchParams.get("promoteType");
  const initialPromoteType: "track" | "playlist" =
    promoteTypeFromQuery === "playlist" ? "playlist" : "track";
  const campaignId = searchParams.get("campaignId");
  const draftId = searchParams.get("draftId");
  const isEditMode = !!campaignId;
  const isDraftMode = !!draftId;
  const isEditOrDraftMode = isEditMode || isDraftMode;

  // Clear currentDraftId from sessionStorage when creating a new campaign
  useEffect(() => {
    if (!isDraftMode) {
      sessionStorage.removeItem("currentDraftId");
    }
  }, [isDraftMode]);

  // Fetch campaign data for edit mode
  const { data: campaignData, isLoading: isLoadingCampaign } =
    useGetMetaBuilderCampaignFromMetaQuery(
      { campaignId: campaignId! },
      { skip: !campaignId },
    );

  // State for draft data to pass to dropdowns
  const [draftCountries, setDraftCountries] = useState<string[]>([]);
  const [draftInterests, setDraftInterests] = useState<any[]>([]);
  const [draftTargeting, setDraftTargeting] = useState<{
    age_min?: number;
    age_max?: number;
    genders?: number[];
  }>({});
  const [draftAdText, setDraftAdText] = useState<{
    message?: string;
    headlineTop?: string;
  }>({});

  const [showSecondSection, setShowSecondSection] = useState(false);
  const [promoteType, setPromoteType] = useState<"track" | "playlist">(
    initialPromoteType,
  );
  const [isLoadingDraft, setIsLoadingDraft] = useState(false);
  const [expandedDropdown, setExpandedDropdown] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const dropdownRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const contentContainerRef = useRef<HTMLDivElement>(null);
  const previewElementRef = useRef<HTMLDivElement>(null);
  const mobilePreviewRef = useRef<HTMLDivElement>(null);
  const [adName, setAdName] = useState<string>("");
  const [spotifyUrl, setSpotifyUrl] = useState<string>("");
  const [artistName, setArtistName] = useState<string>("");
  const [title, setTitle] = useState<string>("");
  const [showAdditionalFields, setShowAdditionalFields] =
    useState<boolean>(false);
  const [audioFileName, setAudioFileName] = useState<string>("");
  const [audioFile, setAudioFile] = useState<File | null>(null);
  const [isAudioUploading, setIsAudioUploading] = useState<boolean>(false);
  const [audioUploadProgress, setAudioUploadProgress] = useState<number>(0);
  const [audioValidationMessage, setAudioValidationMessage] = useState<string>("");
  const [videos, setVideos] = useState<
    Array<{
      id: string;
      fileName: string;
      file?: File;
      isUploading: boolean;
      uploadProgress: number;
      facebookVideoId?: string;
      videoUrl?: string;
      thumbnailUrl?: string;
    }>
  >([]);
  const [selectedVideoId, setSelectedVideoId] = useState<string | null>(null);
  const videoUploadIntervalsRef = useRef<Record<string, NodeJS.Timeout>>({});
  const [landingFileName, setLandingFileName] = useState<string>("");
  const [landingFile, setLandingFile] = useState<File | null>(null);
  const [isLandingUploading, setIsLandingUploading] = useState<boolean>(false);
  const [landingUploadProgress, setLandingUploadProgress] = useState<number>(0);
  const [landingValidationMessage, setLandingValidationMessage] = useState<string>("");
  const [genreValidationMessage, setGenreValidationMessage] = useState<string>("");
  const [isFetchingTrack, setIsFetchingTrack] = useState<boolean>(false);
  const [trackImageUrl, setTrackImageUrl] = useState<string | null>(null);
  const [selectedGenres, setSelectedGenres] = useState<string[]>([]);
  const [selectedCountry, setSelectedCountry] = useState<string | null>(
    "Tier One Countries",
  ); // Default to Tier One Countries
  const [hasSelectedCountry, setHasSelectedCountry] = useState<boolean>(false);
  const [selectedInterests, setSelectedInterests] = useState<
    Array<{ id: string; name: string; audience_size?: number; path?: string[] }>
  >([]);
  const [metaAdsSelections, setMetaAdsSelections] = useState<{
    pageName?: string | null;
    adAccountName?: string | null;
    instagramName?: string | null;
    pixelName?: string | null;
    adAccountId?: string | null;
    pageId?: string | null;
    instagramId?: string | null;
    pixelId?: string | null;
    pageImageUrl?: string | null;
    instagramProfileImageUrl?: string | null;
  }>({});
  const [audienceAge, setAudienceAge] = useState<string>("All age");
  const [audienceGender, setAudienceGender] = useState<string>("All");
  const [adText, setAdText] = useState<string>("");
  const [adHeadline, setAdHeadline] = useState<string>(
    "Listen now on Spotify!",
  );
  const [campaignBudget, setCampaignBudget] = useState<string>("");
  const [currency, setCurrency] = useState<string>("");

  // Wrapper to log budget changes
  const handleBudgetChange = (newBudget: string) => {
    setCampaignBudget(newBudget);
  };
  const [campaignStartDate, setCampaignStartDate] = useState<string>(
    "None (run when published)",
  );
  const [campaignEndDate, setCampaignEndDate] = useState<string>(
    "None (run until turned off manually)",
  );
  const [isScheduled, setIsScheduled] = useState<"always" | "scheduled">(
    "always",
  );
  const [timezone, setTimezone] = useState<string>("Helsinki, Finland (GMT+2)");
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [startTime, setStartTime] = useState<string>("00:00");
  const [endDate, setEndDate] = useState<Dayjs | null>(null);
  const [endTime, setEndTime] = useState<string>("00:00");
  const lastProcessedTrackIdRef = useRef<string>("");
  const [previewOffset, setPreviewOffset] = useState<number>(0);
  const [isMobilePreviewOpen, setIsMobilePreviewOpen] =
    useState<boolean>(false);
  const isPlaylistPromote = promoteType === "playlist";
  const isMusicStepComplete =
    spotifyUrl.trim() !== "" &&
    (isPlaylistPromote || (artistName.trim() !== "" && title.trim() !== ""));

  // Normalize Spotify URL and extract track ID using useMemo to prevent unnecessary recalculations
  const normalizedInput = useMemo(() => {
    return spotifyUrl.replace(/\/intl-[a-z]{2}\//i, "/");
  }, [spotifyUrl]);

  const trackId = useMemo(() => {
    if (!normalizedInput.includes("/track/")) return "";
    const parts = normalizedInput.split("/");
    const lastPart = parts[parts.length - 1];
    const [id] = lastPart.split("?");
    return id;
  }, [normalizedInput]);

  // Set up API query hook
  const { refetch } = useGetArtistListQuery<any>(
    { trackId: trackId },
    {
      skip: !trackId || !normalizedInput.includes("/track/"),
    },
  );
  const [checkBannedArtist] = useLazyCheckBannedArtistQuery();

  const fetchAssetAsFile = async (
    assetUrl: string,
    fallbackName: string,
    fallbackContentType?: string,
  ): Promise<File | null> => {
    try {
      const token = localStorage.getItem("token");
      const absoluteUrl = assetUrl.startsWith("http")
        ? assetUrl
        : `${apiBaseUrl}${assetUrl}`;
      const isBackendAssetUrl = !assetUrl.startsWith("http");
      const response = await fetch(absoluteUrl, {
        method: "GET",
        headers:
          token && isBackendAssetUrl
            ? { Authorization: `Bearer ${token}` }
            : {},
      });
      if (!response.ok) {
        throw new Error(`Failed to fetch asset: ${response.status}`);
      }
      const blob = await response.blob();
      const fileName = fallbackName || "asset-file";
      return new File([blob], fileName, {
        type: blob.type || fallbackContentType || "application/octet-stream",
      });
    } catch (error) {
      console.error("Error fetching asset as file:", error);
      return null;
    }
  };

  const handleDropdownToggle = (id: string) => {
    setExpandedDropdown((prev) => (prev === id ? null : id));
  };

  const handleValidationFailed = (missingField: string) => {
    setValidationError(missingField);
    setExpandedDropdown(missingField);
    // Scroll to the dropdown
    setTimeout(() => {
      const element = dropdownRefs.current[missingField];
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 100);
  };

  // Define dropdown order (only include active/visible dropdowns)
  const dropdownOrder = [
    "music",
    "audio",
    "genre",
    "landing",
    "video",
    "countries",
    "interests",
    "budget",
    "advanced",
    "confirmation",
  ];

  // Handle next button click - close current dropdown and open next one
  const handleNext = (currentId: string) => {
    const currentIndex = dropdownOrder.indexOf(currentId);
    if (currentIndex !== -1 && currentIndex < dropdownOrder.length - 1) {
      const nextId = dropdownOrder[currentIndex + 1];
      setExpandedDropdown(nextId);
      // Scroll to next dropdown after a short delay
      setTimeout(() => {
        const nextElement = dropdownRefs.current[nextId];
        if (nextElement) {
          nextElement.scrollIntoView({ behavior: "smooth", block: "nearest" });
        }
      }, 100);
    }
  };

  // Reset fetching state when URL changes significantly
  useEffect(() => {
    if (!normalizedInput.includes("/track/")) {
      setIsFetchingTrack(false);
      lastProcessedTrackIdRef.current = "";
    }
  }, [normalizedInput]);

  // Auto-fetch track data when Spotify URL is pasted
  useEffect(() => {
    if (!trackId || !normalizedInput.includes("/track/")) return;
    if (isFetchingTrack) return;
    // Prevent duplicate calls for the same trackId
    if (lastProcessedTrackIdRef.current === trackId) return;

    const fetchTimeout = setTimeout(async () => {
      try {
        setIsFetchingTrack(true);
        lastProcessedTrackIdRef.current = trackId;
        const response: any = await refetch({ trackId: trackId });

        if (response.status === "fulfilled" && response.data) {
          const trackData = response.data;

          // Check if any artist is banned
          if (trackData.artists && trackData.artists.length > 0) {
            for (const artist of trackData.artists) {
              if (artist.id) {
                try {
                  const bannedCheck = await checkBannedArtist({
                    artistId: artist.id,
                  }).unwrap();
                  if (bannedCheck.isBanned) {
                    // Clear fields if artist is banned
                    setArtistName("");
                    setTitle("");
                    setTrackImageUrl(null);
                    setIsFetchingTrack(false);
                    return;
                  }
                } catch (err) {
                  console.error("Error checking banned artist:", err);
                }
              }
            }
          }

          // Auto-populate artist name and title
          if (trackData.artists && trackData.artists.length > 0) {
            const artistNames = trackData.artists
              .map((artist: any) => artist.name)
              .filter(Boolean);
            setArtistName(artistNames.join(", ") || "");
          }

          if (trackData.name) {
            setTitle(trackData.name);
          }

          // Set track image URL for preview
          if (trackData.album?.images && trackData.album.images.length > 0) {
            // Use medium size image (index 1) or fallback to first image
            const imageUrl =
              trackData.album.images[1]?.url || trackData.album.images[0]?.url;
            setTrackImageUrl(imageUrl || null);
          } else {
            setTrackImageUrl(null);
          }
        }
      } catch (error) {
        console.error("Error fetching track:", error);
        // Reset the ref on error so it can be retried
        lastProcessedTrackIdRef.current = "";
      } finally {
        setIsFetchingTrack(false);
      }
    }, 300);

    return () => clearTimeout(fetchTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trackId]);

  // Handle Spotify URL input change
  const handleSpotifyUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const url = e.target.value;
    setSpotifyUrl(url);

    // Clear validation error when user starts typing
    if (validationError === "music" && url && url.trim().length > 0) {
      setValidationError(null);
    }

    // Show additional fields if URL has any value
    if (url && url.trim().length > 0) {
      setShowAdditionalFields(true);
    } else {
      setShowAdditionalFields(false);
      // Clear additional fields if URL is empty
      setArtistName("");
      setTitle("");
      setTrackImageUrl(null);
    }
  };

  const handleCountryChange = (value: string | null) => {
    setSelectedCountry(value);
    setHasSelectedCountry(true);
    // Clear validation error when user selects a country
    if (validationError === "countries" && value) {
      setValidationError(null);
    }
  };

  const handleInterestsChange = (values: Array<{ id: string; name: string; audience_size?: number; path?: string[] }>) => {
    setSelectedInterests(values);
    // Also update draftInterests when in draft mode to persist changes
    if (isDraftMode) {
      setDraftInterests(values);
    }
    // Clear validation error when user selects interests
    if (validationError === "interests" && values.length > 0) {
      setValidationError(null);
    }
  };

  const handleGenreChange = (values: string[]) => {
    setSelectedGenres(values);
    // Clear validation error when user selects a genre
    if (validationError === "genre" && values.length > 0) {
      setValidationError(null);
    }
  };

  const handleAudioUploadChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    setAudioUploadProgress(0);

    // Only process if a file is actually selected
    // Don't clear existing audio if dialog is closed without selection
    if (!file) {
      return;
    }

    const MAX_AUDIO_SIZE_MB = 100;
    const MAX_AUDIO_SIZE_BYTES = MAX_AUDIO_SIZE_MB * 1024 * 1024;

    // Check file size
    if (file.size > MAX_AUDIO_SIZE_BYTES) {
      setAudioValidationMessage(`Audio file is too large. Maximum allowed size is ${MAX_AUDIO_SIZE_MB} MB.`);
      return;
    }

    // Clear any previous validation error for valid file
    setAudioValidationMessage("");

    setAudioFileName(file.name);
    setAudioFile(file);
    setIsAudioUploading(true);

    let progress = 0;
    const interval = setInterval(() => {
      progress += 15;
      if (progress >= 100) {
        progress = 100;
        setAudioUploadProgress(progress);
        clearInterval(interval);
        setIsAudioUploading(false);
      } else {
        setAudioUploadProgress(progress);
      }
    }, 200);
  };

  const handleRemoveAudioUpload = () => {
    setAudioFileName("");
    setAudioFile(null);
    setIsAudioUploading(false);
    setAudioUploadProgress(0);
    setAudioValidationMessage("");
  };

  const handleVideoUploadChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    const MAX_VIDEOS = 5;

    // Only process if files are actually selected
    // Don't clear existing videos if dialog is closed without selection
    if (!files || files.length === 0) {
      return;
    }

    // Calculate how many videos can still be added
    const currentCount = videos.length;
    const remainingSlots = MAX_VIDEOS - currentCount;
    const filesToAdd = Array.from(files).slice(0, remainingSlots);

    if (filesToAdd.length === 0) {
      return; // Already at max capacity
    }

    // Add new videos with uploading state
    const newVideos = filesToAdd.map((file) => {
      const videoId = `${Date.now()}-${Math.random()}`;
      return {
        id: videoId,
        fileName: file.name,
        file: file,
        isUploading: true,
        uploadProgress: 0,
      };
    });

    setVideos((prev) => [...prev, ...newVideos]);

    // Simulate upload progress for each new video
    newVideos.forEach((video) => {
      let progress = 0;
      const interval = setInterval(() => {
        progress += 15;
        if (progress >= 100) {
          progress = 100;
          setVideos((prev) => {
            const updatedVideos = prev.map((v) =>
              v.id === video.id
                ? { ...v, isUploading: false, uploadProgress: 100 }
                : v,
            );

            // Always select the first completed video (index 0) when videos are available
            const completedVideos = updatedVideos.filter((v) => !v.isUploading);
            if (completedVideos.length > 0) {
              setSelectedVideoId(completedVideos[0].id);
            }

            return updatedVideos;
          });
          if (videoUploadIntervalsRef.current[video.id]) {
            clearInterval(videoUploadIntervalsRef.current[video.id]);
            delete videoUploadIntervalsRef.current[video.id];
          }
        } else {
          setVideos((prev) =>
            prev.map((v) =>
              v.id === video.id ? { ...v, uploadProgress: progress } : v,
            ),
          );
        }
      }, 200);
      videoUploadIntervalsRef.current[video.id] = interval;
    });
  };

  const handleRemoveVideo = (videoId: string) => {
    // Clear interval if video is still uploading
    if (videoUploadIntervalsRef.current[videoId]) {
      clearInterval(videoUploadIntervalsRef.current[videoId]);
      delete videoUploadIntervalsRef.current[videoId];
    }
    // Remove video from state
    setVideos((prev) => prev.filter((v) => v.id !== videoId));
  };

  const handleLandingUploadChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    isValid: boolean = true,
    validationMessage?: string,
  ) => {
    const file = e.target.files?.[0];
    setLandingUploadProgress(0);

    // Only process if a file is actually selected
    // Don't clear existing landing image if dialog is closed without selection
    if (!file) {
      return;
    }

    // Handle validation result from child component
    if (!isValid && validationMessage) {
      setLandingValidationMessage(validationMessage);
      return;
    }

    // Clear any previous validation error for valid file
    setLandingValidationMessage("");

    setLandingFileName(file.name);
    setLandingFile(file);
    setIsLandingUploading(true);
    if (validationError === "landing") {
      setValidationError(null);
    }

    let progress = 0;
    const interval = setInterval(() => {
      progress += 20;
      if (progress >= 100) {
        progress = 100;
        setLandingUploadProgress(progress);
        clearInterval(interval);
        setIsLandingUploading(false);
      } else {
        setLandingUploadProgress(progress);
      }
    }, 200);
  };

  const handleRemoveLandingUpload = () => {
    setLandingFileName("");
    setLandingFile(null);
    setIsLandingUploading(false);
    setLandingUploadProgress(0);
    setLandingValidationMessage("");
  };

  // Cleanup upload intervals on unmount
  useEffect(() => {
    return () => {
      Object.values(videoUploadIntervalsRef.current).forEach((interval) => {
        clearInterval(interval);
      });
    };
  }, []);

  // Container-bounded smooth scroll-following effect for preview
  // Keep the preview centered between header-bottom and viewport-bottom
  // across laptop screen heights while still respecting container bounds.
  useEffect(() => {
    let animationFrameId: number;

    const animate = () => {
      const contentContainer = contentContainerRef.current;
      const previewElement = previewElementRef.current;

      if (!contentContainer || !previewElement) {
        animationFrameId = requestAnimationFrame(animate);
        return;
      }

      // Get container and element dimensions
      const containerRect = contentContainer.getBoundingClientRect();
      // Use the preview's visually rendered block height (scaled) for precise centering.
      const renderedPreviewBlock = previewElement.querySelector(
        ".preview-scaler",
      ) as HTMLElement | null;
      const previewHeight =
        renderedPreviewBlock?.getBoundingClientRect().height ||
        previewElement.getBoundingClientRect().height;
      const containerHeight = contentContainer.offsetHeight;

      const headerBottom =
        (
          document.querySelector(".main-wrapper-header") as HTMLElement | null
        )?.getBoundingClientRect().bottom ??
        (
          document.querySelector("header") as HTMLElement | null
        )?.getBoundingClientRect().bottom ??
        0;

      const viewportBottom = window.innerHeight;
      const visibleTop = Math.max(headerBottom, containerRect.top);
      const visibleBottom = Math.min(viewportBottom, containerRect.bottom);
      const middlePoint = visibleTop + (visibleBottom - visibleTop) / 2;
      const desiredTopInViewport = middlePoint - previewHeight / 2;
      const desiredOffset = desiredTopInViewport - containerRect.top;

      // Allow upward movement when container starts below header-bottom,
      // otherwise preview gets stuck too low on shorter laptop heights.
      const minOffset = -98;
      const maxScrollableDistance = Math.max(
        0,
        containerHeight - previewHeight,
      );
      // Lift the preview slightly so there’s more even breathing room above/below it.
      const offsetBias = 60; // px
      const targetOffset = Math.min(
        Math.max(minOffset, desiredOffset - offsetBias),
        maxScrollableDistance,
      );

      // Apply smooth easing
      setPreviewOffset((currentOffset) => {
        const diff = targetOffset - currentOffset;
        const easingFactor = 0.1; // Smooth easing
        const newOffset = currentOffset + diff * easingFactor;

        // Stop if very close to target to prevent infinite micro-movements
        if (Math.abs(diff) < 0.5) {
          return targetOffset;
        }

        return newOffset;
      });

      animationFrameId = requestAnimationFrame(animate);
    };

    // Start animation loop
    animationFrameId = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  // Pre-fill form data when editing a campaign
  useEffect(() => {
    if (isEditMode && campaignData) {
      const { campaign, account, countries, linkUrls, adSets, ads, metadata } =
        campaignData;
      if (metadata?.promoteType === "playlist") {
        setPromoteType("playlist");
      } else if (metadata?.promoteType === "track") {
        setPromoteType("track");
      }

      if (metadata?.genres && Array.isArray(metadata.genres) && metadata.genres.length > 0) {
        setSelectedGenres(metadata.genres);
      }
      if (metadata?.audioAsset?.fileName) {
        setAudioFileName(metadata.audioAsset.fileName);
      }
      if (metadata?.landingAsset?.fileName) {
        setLandingFileName(metadata.landingAsset.fileName);
      }
      // Load audio/landing assets from campaign API stream endpoints so they are auto-selected in edits mode.
      (async () => {
        if (metadata?.audioAsset?.streamUrl) {
          const audioFromApi = await fetchAssetAsFile(
            metadata.audioAsset.streamUrl,
            metadata.audioAsset.fileName || "campaign-audio.mp3",
            metadata.audioAsset.contentType,
          );
          if (audioFromApi) {
            setAudioFile(audioFromApi);
          }
        }
        if (metadata?.landingAsset?.streamUrl) {
          const landingFromApi = await fetchAssetAsFile(
            metadata.landingAsset.streamUrl,
            metadata.landingAsset.fileName || "campaign-landing.png",
            metadata.landingAsset.contentType,
          );
          if (landingFromApi) {
            setLandingFile(landingFromApi);
          }
        }
      })();

      // Pre-fill page and Instagram account names and images from campaign
      // metadata. The ids matter as much as the stored image URLs: campaigns
      // built before those URLs were captured — and ones whose URL has since
      // expired — let the preview re-resolve the picture from the id instead.
      if (metadata?.pageName || metadata?.instagramName ||
          metadata?.pageImageUrl || metadata?.instagramProfileImageUrl ||
          campaignData?.pageId || campaignData?.instagramAccountId) {
        setMetaAdsSelections((prev) => ({
          ...prev,
          pageName: metadata?.pageName || prev.pageName,
          instagramName: metadata?.instagramName || prev.instagramName,
          pageImageUrl: metadata?.pageImageUrl || prev.pageImageUrl,
          instagramProfileImageUrl: metadata?.instagramProfileImageUrl || prev.instagramProfileImageUrl,
          pageId: campaignData?.pageId || prev.pageId,
          instagramId: campaignData?.instagramAccountId || prev.instagramId,
        }));
      }

      // Pre-fill basic fields from campaign
      setAdName(campaign.name || "");

      // Pre-fill budget (daily_budget or lifetime_budget from single campaign endpoint is a string in cents)
      const budgetValue = campaign.daily_budget || campaign.lifetime_budget;
      if (budgetValue) {
        // Handle both string and number types
        const budgetInCents =
          typeof budgetValue === "string"
            ? parseFloat(budgetValue)
            : budgetValue;
        const budgetInEuros = budgetInCents / 100;
        setCampaignBudget(budgetInEuros.toString());
      }

      // Pre-fill schedule - check if campaign has stop_time (scheduled) or not (always running)
      if (campaign.stop_time) {
        // Campaign is scheduled with end date
        setIsScheduled("scheduled");
        setStartDate(dayjs(campaign.start_time));
        setEndDate(dayjs(campaign.stop_time));

        // Set times from the start and end time
        const startFormatted = dayjs(campaign.start_time).format("HH:mm");
        const endFormatted = dayjs(campaign.stop_time).format("HH:mm");
        setStartTime(startFormatted);
        setEndTime(endFormatted);
      } else {
        // Campaign is always running (no stop_time)
        setIsScheduled("always");
        setStartDate(dayjs(campaign.start_time));
      }

      // Pre-fill timezone from account
      if (account.timezone_name) {
        // Format timezone for display (e.g., "Europe/Helsinki" -> "Helsinki, Finland (GMT+2)")
        // For now, just use the timezone_name directly or map it
        setTimezone(account.timezone_name);
      }

      // Pre-fill currency from account
      if (account.currency) {
        setCurrency(account.currency);
      }

      // Pre-fill music URL: use original Spotify URL from our DB when present, else link from Meta (smart link)
      const musicUrl = campaignData.originalSpotifyUrl ?? linkUrls?.[0] ?? "";
      if (musicUrl) {
        setSpotifyUrl(musicUrl);
        setArtistName(campaignData.originalArtistName ?? "");
        setTitle(campaignData.originalSongName ?? "");
        setTrackImageUrl(null);

        // Extract track ID from URL and fetch track data immediately (for image etc.)
        if (musicUrl.includes("/track/")) {
          const parts = musicUrl.split("/track/");
          const trackId = parts[1]?.split("?")[0];
          if (trackId) {
            // Reset the ref to allow re-fetching this track in edit mode
            lastProcessedTrackIdRef.current = "";
            setShowAdditionalFields(true);

            // Manually trigger track data fetch for edit mode
            setTimeout(async () => {
              try {
                setIsFetchingTrack(true);
                lastProcessedTrackIdRef.current = trackId;

                const response: any = await refetch({ trackId: trackId });

                if (response.status === "fulfilled" && response.data) {
                  const trackData = response.data;

                  // Check if any artist is banned
                  if (trackData.artists && trackData.artists.length > 0) {
                    for (const artist of trackData.artists) {
                      if (artist.id) {
                        try {
                          const bannedCheck = await checkBannedArtist({
                            artistId: artist.id,
                          }).unwrap();
                          if (bannedCheck.isBanned) {
                            // Clear fields if artist is banned
                            setArtistName("");
                            setTitle("");
                            setTrackImageUrl(null);
                            setIsFetchingTrack(false);
                            return;
                          }
                        } catch (err) {
                          console.error("Error checking banned artist:", err);
                        }
                      }
                    }
                  }

                  // Auto-populate artist name and title
                  if (trackData.artists && trackData.artists.length > 0) {
                    const artistNames = trackData.artists
                      .map((artist: any) => artist.name)
                      .filter(Boolean);
                    setArtistName(artistNames.join(", ") || "");
                  }

                  if (trackData.name) {
                    setTitle(trackData.name);
                  }

                  // Set track image URL for preview
                  if (
                    trackData.album?.images &&
                    trackData.album.images.length > 0
                  ) {
                    // Use medium size image (index 1) or fallback to first image
                    const imageUrl =
                      trackData.album.images[1]?.url ||
                      trackData.album.images[0]?.url;
                    setTrackImageUrl(imageUrl || null);
                  } else {
                    setTrackImageUrl(null);
                  }
                }
              } catch (error) {
                console.error("Error fetching track in edit mode:", error);
              } finally {
                setIsFetchingTrack(false);
              }
            }, 100);
          }
        }
      }

      // Countries and interests are now handled directly by the dropdowns via props

      // Pre-fill targeting (age)
      if (adSets && adSets.length > 0) {
        const adSet = adSets[0];
        const targeting = adSet.targeting;

        // Pre-fill targeting (age)
        if (targeting.age_min && targeting.age_max) {
          setAudienceAge(`${targeting.age_min}-${targeting.age_max}`);
        }
      }

      // Pre-fill ad creative from ads
      if (ads && ads.length > 0) {
        const ad = ads[0];
        const creative = ad.creative.object_story_spec;

        // Pre-fill ad text (message)
        if (creative.video_data.message) {
          setAdText(creative.video_data.message);
        }

        // Pre-fill ad headline (title)
        if (creative.video_data.title) {
          setAdHeadline(creative.video_data.title);
        }

        // Set draftAdText for AdvancedSettingsDropdown
        setDraftAdText({
          message: creative.video_data.message || "",
          headlineTop: creative.video_data.title || "",
        });

        // Pre-fill track image URL
        if (creative.video_data.image_url) {
          setTrackImageUrl(creative.video_data.image_url);
        }

        // Pre-fill page ID
        if (creative.page_id) {
          setMetaAdsSelections((prev) => ({
            ...prev,
            pageId: creative.page_id,
          }));
        }

        // Extract video IDs from ads to fetch video details
        const videoIds = ads
          .map((ad: any) => ad.creative.object_story_spec?.video_data?.video_id)
          .filter(Boolean);

        // Fetch videos by IDs if we have any
        if (videoIds.length > 0) {
          fetchVideosByIds(videoIds);
        }
      }
    }
  }, [isEditMode, campaignData]);

  // Pre-fill form data when restoring a draft
  useEffect(() => {
    const loadDraftData = async () => {
      if (!isDraftMode || !draftId) return;

      try {
        setIsLoadingDraft(true);
        const token = localStorage.getItem("token");
        const response = await fetch(
          `${apiBaseUrl}/api/meta-builder/campaigns/draft/${draftId}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          },
        );

        if (!response.ok) {
          throw new Error("Failed to fetch draft");
        }

        const result = await response.json();
        const draft = result.draft || result.data; // Support both 'draft' and 'data' keys

        // Pre-fill basic fields from draft: use backend originalSpotifyUrl, fallback to draft if empty (never show smart link)
        setAdName(draft.campaignName || "");
        const isSmartLinkUrl = (url: string) =>
          url && typeof url === "string" && url.includes("meta-ads-referral");
        const linkUrl = draft.trackInfo?.linkUrl || "";
        const metaSpotify = draft.metadata?.spotifyUrl || "";
        const fallbackUrl =
          linkUrl && !isSmartLinkUrl(linkUrl)
            ? linkUrl
            : metaSpotify && !isSmartLinkUrl(metaSpotify)
              ? metaSpotify
              : "";
        const draftSpotifyUrl =
          (result.originalSpotifyUrl &&
          !isSmartLinkUrl(result.originalSpotifyUrl)
            ? result.originalSpotifyUrl
            : "") || fallbackUrl;
        if (draft.metadata?.promoteType === "playlist") {
          setPromoteType("playlist");
        } else if (draft.metadata?.promoteType === "track") {
          setPromoteType("track");
        }
        setSpotifyUrl(draftSpotifyUrl);
        setArtistName(
          result.originalArtistName ?? draft.metadata?.artistName ?? "",
        );
        setTitle(result.originalSongName ?? draft.metadata?.title ?? "");
        const restoredGenres = Array.isArray(draft.metadata?.genres)
          ? draft.metadata.genres
          : Array.isArray(draft.metadata?.draftData?.originalData?.genres)
            ? draft.metadata.draftData.originalData.genres
            : [];
        if (restoredGenres.length > 0) {
          setSelectedGenres(restoredGenres);
        }
        if (draft.metadata?.audioAsset?.fileName) {
          setAudioFileName(draft.metadata.audioAsset.fileName);
          const draftAudioUrl =
            draft.metadata.audioAsset.streamUrl ||
            draft.metadata.audioAsset.url;
          if (draftAudioUrl) {
            const audioFromDraft = await fetchAssetAsFile(
              draftAudioUrl,
              draft.metadata.audioAsset.fileName,
              draft.metadata.audioAsset.contentType,
            );
            if (audioFromDraft) {
              setAudioFile(audioFromDraft);
            }
          }
        }
        if (draft.metadata?.landingAsset?.fileName) {
          setLandingFileName(draft.metadata.landingAsset.fileName);
          const draftLandingUrl =
            draft.metadata.landingAsset.streamUrl ||
            draft.metadata.landingAsset.url;
          if (draftLandingUrl) {
            const landingFromDraft = await fetchAssetAsFile(
              draftLandingUrl,
              draft.metadata.landingAsset.fileName,
              draft.metadata.landingAsset.contentType,
            );
            if (landingFromDraft) {
              setLandingFile(landingFromDraft);
            }
          }
        }

        // Pre-fill page and Instagram account names and images from draft metadata
        if (draft.metadata?.pageName || draft.metadata?.instagramName ||
            draft.metadata?.pageImageUrl || draft.metadata?.instagramProfileImageUrl) {
          setMetaAdsSelections((prev) => ({
            ...prev,
            pageName: draft.metadata?.pageName || prev.pageName,
            instagramName: draft.metadata?.instagramName || prev.instagramName,
            pageImageUrl: draft.metadata?.pageImageUrl || prev.pageImageUrl,
            instagramProfileImageUrl: draft.metadata?.instagramProfileImageUrl || prev.instagramProfileImageUrl,
          }));
        }

        // Pre-fill pixel ID and name from draft
        if (draft.pixelId || draft.metadata?.pixelName) {
          setMetaAdsSelections((prev) => ({
            ...prev,
            pixelId: draft.pixelId || prev.pixelId,
            pixelName: draft.metadata?.pixelName || prev.pixelName,
          }));
        }

        // Show additional fields if draft has Spotify URL
        if (draftSpotifyUrl.trim().length > 0) {
          setShowAdditionalFields(true);
        }

        // Pre-fill budget
        if (
          draft.budget?.amount !== undefined &&
          draft.budget?.amount !== null
        ) {
          setCampaignBudget(Number(draft.budget.amount).toFixed(2));
        }

        // Pre-fill schedule
        if (draft.schedule?.isScheduled) {
          setIsScheduled("scheduled");
          if (draft.schedule.startDate) {
            setStartDate(dayjs(draft.schedule.startDate));
          }
          if (draft.schedule.endDate) {
            setEndDate(dayjs(draft.schedule.endDate));
          }
          setStartTime(draft.schedule.startTime || "00:00");
          setEndTime(draft.schedule.endTime || "00:00");
        } else {
          setIsScheduled("always");
        }

        // Pre-fill timezone
        if (draft.schedule?.timezone) {
          setTimezone(draft.schedule.timezone);
        }

        // Pre-fill targeting - countries
        if (
          draft.targeting?.countries &&
          draft.targeting.countries.length > 0
        ) {
          const firstCountry = draft.targeting.countries[0];

          // Check if it's a tier name (e.g., "Tier One Countries")
          const tierMapping: Record<string, string> = {
            "Tier One Countries": "tier-one",
            "Tier Two Countries": "tier-two",
            "Tier Three Countries": "tier-three",
            "Tier One and Two Countries": "tier-one-two",
          };

          if (tierMapping[firstCountry]) {
            // It's a tier name - set it directly
            console.log("Tier name detected, setting:", firstCountry);
            setSelectedCountry(firstCountry);
            setDraftCountries([firstCountry]); // Pass tier name to dropdown
          } else {
            // It's individual country names (rare case - user manually selected specific countries)
            console.log("Individual countries detected");
            const countryString = draft.targeting.countries.join(", ");
            setSelectedCountry(countryString);
            setDraftCountries(draft.targeting.countries);
          }

          // Set hasSelectedCountry to true when loading from draft
          setHasSelectedCountry(true);
        }

        // Pre-fill targeting - interests
        if (
          draft.targeting?.interests &&
          draft.targeting.interests.length > 0
        ) {
          console.log(
            "Setting selectedInterests to:",
            draft.targeting.interests,
          );
          setSelectedInterests(draft.targeting.interests);
          setDraftInterests(draft.targeting.interests);
        }

        // Pre-fill audience age and gender
        if (
          draft.targeting?.age_min ||
          draft.targeting?.age_max ||
          draft.targeting?.genders
        ) {
          setDraftTargeting({
            age_min: draft.targeting.age_min,
            age_max: draft.targeting.age_max,
            genders: draft.targeting.genders,
          });

          // Also update the main state for consistency
          if (draft.targeting.age_min && draft.targeting.age_max) {
            const ageRange = `${draft.targeting.age_min}-${draft.targeting.age_max}`;
            setAudienceAge(ageRange);
          }
          if (draft.targeting.genders && draft.targeting.genders.length > 0) {
            const gender = draft.targeting.genders[0] === 1 ? "Male" : "Female";
            setAudienceGender(gender);
          }
        }

        // Pre-fill ad text and headline
        if (draft.adText?.message || draft.adText?.headlineTop) {
          setDraftAdText({
            message: draft.adText.message,
            headlineTop: draft.adText.headlineTop,
          });

          // Also update the main state for consistency
          if (draft.adText?.message) {
            setAdText(draft.adText.message);
          }
          if (draft.adText?.headlineTop) {
            setAdHeadline(draft.adText.headlineTop);
          }
        }

        // Load videos from draft
        if (result.videos && result.videos.length > 0) {
          console.log("=== DRAFT VIDEOS ===");
          console.log("Videos array:", result.videos);

          const draftVideos = result.videos.map((video: any) => ({
            id: video.key || video.url, // Use S3 key or URL as unique ID
            fileName: video.fileName || "Draft Video",
            file: undefined, // No File object for S3 videos
            isUploading: false,
            uploadProgress: 100,
            videoUrl: `${apiBaseUrl}${video.url}`, // Full streaming URL
            thumbnailUrl: undefined, // No thumbnail from draft API
          }));

          setVideos(draftVideos);

          // Auto-select first video if available
          if (draftVideos.length > 0 && !selectedVideoId) {
            setSelectedVideoId(draftVideos[0].id);
          }
        } else {
          console.log("No videos found in draft");
        }

        // Store draft ID for reference (e.g., for updating instead of creating new)
        sessionStorage.setItem("currentDraftId", draft._id);
      } catch (error) {
        console.error("Error loading draft:", error);
      } finally {
        setIsLoadingDraft(false);
      }
    };

    loadDraftData();
  }, [isDraftMode, draftId]);

  // All 4 required for preview: music, audio, landing, ad creative (video)
  const isAudioComplete = audioFileName.trim() !== "";
  const isAdCreativeComplete = videos.length > 0;
  const isPreviewReady =
    showSecondSection &&
    isMusicStepComplete &&
    landingFileName.trim() !== "" &&
    isAudioComplete &&
    isAdCreativeComplete;

  // Close mobile preview when any of the 4 required fields is missing
  useEffect(() => {
    if (!isPreviewReady) {
      setIsMobilePreviewOpen(false);
    }
  }, [isPreviewReady]);

  // Function to fetch videos by their IDs
  const fetchVideosByIds = async (videoIds: string[]) => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${apiBaseUrl}/api/meta-builder/videos/by-ids`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ videoIds }),
        },
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch videos: ${response.statusText}`);
      }

      const data = await response.json();
      console.log("Videos data received:", data);

      // Transform the videos data to match the VideoItem structure
      const videoItems = data.videos.map((video: any) => {
        // Get the preferred thumbnail or fallback to first thumbnail
        const preferredThumbnail = video.thumbnails?.data?.find(
          (thumb: any) => thumb.is_preferred,
        );
        const thumbnail = preferredThumbnail || video.thumbnails?.data?.[0];

        return {
          id: video.id,
          fileName: `Video ${video.id}`,
          file: undefined, // No file object since it's from Facebook
          isUploading: false,
          uploadProgress: 100,
          facebookVideoId: video.id,
          videoUrl: video.source, // Video source URL
          thumbnailUrl: thumbnail?.uri || "", // Thumbnail URL
        };
      });

      // Set the videos state with fetched videos
      setVideos(videoItems);
    } catch (error) {
      console.error("Error fetching videos by IDs:", error);
    }
  };

  // Show loading state while fetching campaign data in edit mode
  if (isEditMode && isLoadingCampaign) {
    return (
      <div className="w-full min-h-screen flex flex-col items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-16 h-16 border-4 border-[#58F2A9] border-t-transparent rounded-full animate-spin"></div>
          <p className="text-white text-lg">Loading campaign data...</p>
        </div>
      </div>
    );
  }

  // Show loading state while fetching draft data
  if (isDraftMode && isLoadingDraft) {
    return (
      <div className="w-full min-h-screen flex flex-col items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-16 h-16 border-4 border-[#58F2A9] border-t-transparent rounded-full animate-spin"></div>
          <p className="text-white text-lg">Loading draft data...</p>
        </div>
      </div>
    );
  }

  return (
    <SubscriptionProtectedPage>
      <div className="min-h-screen pt-4 mt-[10px] sm:pt-6 md:pt-10 pb-12 sm:pb-10 overflow-x-hidden flex justify-center lg:px-4">
        <div className="flex justify-center w-full">
          <div className="w-full max-w-[1308px] flex flex-col gap-3 sm:gap-4">
            <AdNameNavbar
              onBack={
                showSecondSection
                  ? () => {
                      setShowSecondSection(false);
                      setExpandedDropdown(null);
                    }
                  : undefined
              }
            />
            <AdNameStepBar showSecondSection={showSecondSection} />

            {/* Section 1 */}
            {!showSecondSection && (
              <AdNameSection
                onNext={() => setShowSecondSection(true)}
                adName={adName}
                onAdNameChange={setAdName}
                promoteType={promoteType}
                onPromoteTypeChange={setPromoteType}
                isEditMode={isEditMode}
              />
            )}

            {/* NEW Section 2 (background image only) */}
            {showSecondSection && (
              <section
                className="relative z-[2] w-full max-w-[1308px] mx-auto rounded-[28px] md:rounded-[40px] p-[2px]"
                style={{
                  background:
                    "linear-gradient(180deg, #58F2A9 0%, #1B76FF 79.33%, rgba(255, 170, 45, 0.1) 95.19%)",
                }}
              >
                <div className="relative w-full h-auto rounded-[26px] md:rounded-[38px] overflow-hidden bg-[#0B0D12]">
                  {/* Centered Top Gradient - responsive size (same as meta-ads-submitting-modal) */}
                  <div
                    className="absolute left-1/2 -translate-x-1/2 opacity-80 blur-2xl sm:blur-3xl w-[280px] h-[90px] sm:w-[360px] sm:h-[110px] md:w-[460px] md:h-[140px]"
                    style={{
                      top: "-50px",
                      background:
                        "linear-gradient(226.45deg, #1B76FF 12.8%, #2BFAA3 93.65%)",
                    }}
                  />
                  {/* Background image layer */}
                  <div
                    className="absolute inset-0 bg-cover bg-no-repeat bg-center"
                    // style={{ backgroundImage: `url(${mabAd2})` }}
                  />

                  {/* Dark overlay to fully cover the background */}
                  <div className="absolute inset-0 bg-[#0B0D12]/80" />

                  {/* Content — mobile 16px padding per Figma (390px frame) */}
                  <div className="relative z-10 w-full h-auto px-4 sm:px-10 md:px-14 lg:px-20 py-6 sm:py-8 md:py-14">
                    {/* MAIN WRAPPER */}
                    <h2
                      className="text-white font-bold text-[24px] text-center lg:text-start lg:text-[48px]
                         leading-[100%] mb-5 sm:mb-6 md:mb-8"
                    >
                      {isDraftMode
                        ? "Restore Draft"
                        : isEditMode
                          ? "Edit Campaign"
                          : promoteType === "playlist"
                            ? "Grow My Spotify Playlist"
                            : "Grow My Spotify Track"}
                    </h2>
                    <div
                      ref={contentContainerRef}
                      className="flex flex-col lg:flex-row gap-10"
                    >
                      {/* LEFT CONTENT — DROPDOWNS */}
                      {(() => {
                        return (
                          <div
                            className={`flex-1 w-full min-w-0 lg:max-w-[1100px] order-2 lg:order-1 ml-0 ${isPreviewReady ? "lg:ml-[-10px]" : "lg:ml-[18px]"}`}
                          >
                            <div className="flex flex-col gap-2 sm:gap-3">
                              {/* Music */}
                              <div
                                ref={(el) =>
                                  (dropdownRefs.current["music"] = el)
                                }
                              >
                                <MusicDropdown
                                  id="music"
                                  expandedId={expandedDropdown}
                                  onToggle={handleDropdownToggle}
                                  onNext={handleNext}
                                  promoteType={promoteType}
                                  spotifyUrl={spotifyUrl}
                                  artistName={artistName}
                                  title={title}
                                  showAdditionalFields={showAdditionalFields}
                                  onSpotifyUrlChange={handleSpotifyUrlChange}
                                  onArtistNameChange={(e) =>
                                    setArtistName(e.target.value)
                                  }
                                  onTitleChange={(e) =>
                                    setTitle(e.target.value)
                                  }
                                  isEditMode={isEditMode}
                                  showValidationMessage={validationError === "music"}
                                  validationMessage={
                                    validationError === "music"
                                      ? "Please provide a source link to create your campaign"
                                      : undefined
                                  }
                                />
                              </div>

                              {/* Audio */}
                              <div
                                ref={(el) =>
                                  (dropdownRefs.current["audio"] = el)
                                }
                              >
                                <AudioDropdown
                                  id="audio"
                                  expandedId={expandedDropdown}
                                  onToggle={handleDropdownToggle}
                                  onNext={handleNext}
                                  audioFileName={audioFileName}
                                  onAudioUploadChange={handleAudioUploadChange}
                                  isAudioUploading={isAudioUploading}
                                  audioUploadProgress={audioUploadProgress}
                                  onAudioRemove={handleRemoveAudioUpload}
                                  isEditMode={isEditMode}
                                  showValidationMessage={!!audioValidationMessage}
                                  validationMessage={audioValidationMessage}
                                />
                              </div>

                              {/* Genre */}
                              <div
                                ref={(el) =>
                                  (dropdownRefs.current["genre"] = el)
                                }
                              >
                                <TrackGenreDropdown
                                  id="genre"
                                  expandedId={expandedDropdown}
                                  onToggle={handleDropdownToggle}
                                  onNext={handleNext}
                                  onSelectionChange={handleGenreChange}
                                  initialSelectedGenres={selectedGenres}
                                  isEditMode={isEditMode}
                                  showValidationMessage={validationError === "genre"}
                                  validationMessage="Please choose the main genre of your track or playlist"
                                />
                              </div>

                              {/* Landing */}
                              <div
                                ref={(el) =>
                                  (dropdownRefs.current["landing"] = el)
                                }
                              >
                                <LandingDropdown
                                  id="landing"
                                  expandedId={expandedDropdown}
                                  onToggle={handleDropdownToggle}
                                  onNext={handleNext}
                                  landingFileName={landingFileName}
                                  onLandingUploadChange={
                                    handleLandingUploadChange
                                  }
                                  isLandingUploading={isLandingUploading}
                                  landingUploadProgress={landingUploadProgress}
                                  onLandingRemove={handleRemoveLandingUpload}
                                  isEditMode={isEditMode}
                                  showValidationMessage={
                                    validationError === "landing" &&
                                    landingFileName.trim() === "" &&
                                    !isEditMode
                                  }
                                  validationMessage={
                                    landingValidationMessage || undefined
                                  }
                                />
                              </div>
                              {/* Video */}
                              <div
                                ref={(el) =>
                                  (dropdownRefs.current["video"] = el)
                                }
                              >
                                <VideoDropdown
                                  id="video"
                                  expandedId={expandedDropdown}
                                  onToggle={handleDropdownToggle}
                                  onNext={handleNext}
                                  videos={videos}
                                  onVideoUploadChange={handleVideoUploadChange}
                                  onRemoveVideo={handleRemoveVideo}
                                  isEditMode={isEditMode}
                                  selectedVideoId={selectedVideoId}
                                  onVideoSelect={setSelectedVideoId}
                                  onPreviewScreen={() => {
                                    if (!isPreviewReady) return;
                                    setIsMobilePreviewOpen(true);
                                    requestAnimationFrame(() => {
                                      mobilePreviewRef.current?.scrollIntoView({
                                        behavior: "smooth",
                                        block: "start",
                                      });
                                    });
                                  }}
                                />
                              </div>

                              {/* Countries */}
                              <div
                                ref={(el) =>
                                  (dropdownRefs.current["countries"] = el)
                                }
                              >
                                <CountriesDropdown
                                  id="countries"
                                  expandedId={expandedDropdown}
                                  onToggle={handleDropdownToggle}
                                  onCountryChange={handleCountryChange}
                                  onNext={handleNext}
                                  initialSelectedCountries={
                                    isEditMode
                                      ? campaignData?.countries || undefined
                                      : isDraftMode && draftCountries.length > 0
                                        ? draftCountries
                                        : undefined
                                  }
                                  isEditMode={isEditMode} // Only disable for edit mode, not draft mode
                                  showValidationMessage={validationError === "countries"}
                                  validationMessage={
                                    validationError === "countries"
                                      ? "Please select at least one option from the country/tier dropdown menu"
                                      : undefined
                                  }
                                />
                              </div>

                              {/* Interests */}
                              <div
                                ref={(el) =>
                                  (dropdownRefs.current["interests"] = el)
                                }
                              >
                                <InterestsDropdown
                                  id="interests"
                                  expandedId={expandedDropdown}
                                  onToggle={handleDropdownToggle}
                                  onNext={handleNext}
                                  onInterestsChange={handleInterestsChange}
                                  initialInterests={
                                    isEditMode
                                      ? (() => {
                                          const targeting =
                                            campaignData?.adSets?.[0]
                                              ?.targeting;
                                          console.log(
                                            "🔍 Edit Mode Interests Debug:",
                                            {
                                              hasCampaignData: !!campaignData,
                                              hasAdSets: !!campaignData?.adSets,
                                              adSetsLength:
                                                campaignData?.adSets?.length ||
                                                0,
                                              hasFirstAdSet:
                                                !!campaignData?.adSets?.[0],
                                              hasTargeting: !!targeting,
                                              targeting: targeting,
                                              hasFlexibleSpec:
                                                !!targeting?.flexible_spec,
                                              flexibleSpecLength:
                                                targeting?.flexible_spec
                                                  ?.length || 0,
                                              hasDirectInterests:
                                                !!targeting?.interests,
                                            },
                                          );

                                          // Try to get interests from flexible_spec first (new API format)
                                          let interests:
                                            | Array<{
                                                id: string;
                                                name: string;
                                              }>
                                            | undefined = undefined;

                                          if (
                                            targeting?.flexible_spec &&
                                            Array.isArray(
                                              targeting.flexible_spec,
                                            )
                                          ) {
                                            // Combine all interests from all flexible_spec items
                                            const allInterests: Array<{
                                              id: string;
                                              name: string;
                                            }> = [];
                                            targeting.flexible_spec.forEach(
                                              (spec: any) => {
                                                if (
                                                  spec.interests &&
                                                  Array.isArray(spec.interests)
                                                ) {
                                                  allInterests.push(
                                                    ...spec.interests,
                                                  );
                                                }
                                              },
                                            );

                                            if (allInterests.length > 0) {
                                              interests = allInterests;
                                              console.log(
                                                "✅ Found interests in flexible_spec:",
                                                interests,
                                              );
                                            }
                                          }

                                          // Fallback to direct interests array (old API format)
                                          if (
                                            !interests &&
                                            targeting?.interests &&
                                            Array.isArray(
                                              targeting.interests,
                                            ) &&
                                            targeting.interests.length > 0
                                          ) {
                                            interests = targeting.interests;
                                            console.log(
                                              "✅ Found interests in direct array:",
                                              interests,
                                            );
                                          }

                                          console.log(
                                            "📊 Final interests to pass:",
                                            interests,
                                          );
                                          return interests;
                                        })()
                                      : isDraftMode && draftInterests.length > 0
                                        ? draftInterests
                                        : undefined
                                  }
                                  isEditMode={isEditMode} // Only disable for edit mode, not draft mode
                                  isDraftMode={isDraftMode} // Allow modification in draft mode
                                  showValidationMessage={validationError === "interests"}
                                  validationMessage={
                                    validationError === "interests"
                                      ? "Please choose at least one interest group for ad targeting"
                                      : undefined
                                  }
                                />
                              </div>

                              {/* Budget */}
                              <div
                                ref={(el) =>
                                  (dropdownRefs.current["budget"] = el)
                                }
                              >
                                <BudgetDropdown
                                  id="budget"
                                  expandedId={expandedDropdown}
                                  onToggle={handleDropdownToggle}
                                  onNext={handleNext}
                                  onBudgetChange={handleBudgetChange}
                                  onCurrencyChange={setCurrency}
                                  onStartDateChange={setCampaignStartDate}
                                  onEndDateChange={setCampaignEndDate}
                                  onScheduleChange={setIsScheduled}
                                  onTimezoneChange={setTimezone}
                                  onStartDateTimeChange={(date, time) => {
                                    setStartDate(date);
                                    setStartTime(time);
                                  }}
                                  onEndDateTimeChange={(date, time) => {
                                    setEndDate(date);
                                    setEndTime(time);
                                  }}
                                  isEditMode={isEditMode}
                                  initialBudget={campaignBudget}
                                  initialSchedule={isScheduled}
                                  initialTimezone={timezone}
                                  initialStartDate={startDate}
                                  initialEndDate={endDate}
                                  initialStartTime={startTime}
                                  initialEndTime={endTime}
                                  initialCurrency={currency}
                                  adAccountId={
                                    metaAdsSelections.adAccountId ?? undefined
                                  }
                                />
                              </div>

                              {/* Advanced */}
                              <div
                                ref={(el) =>
                                  (dropdownRefs.current["advanced"] = el)
                                }
                              >
                                <AdvancedSettingsDropdown
                                  id="advanced"
                                  expandedId={expandedDropdown}
                                  onToggle={handleDropdownToggle}
                                  onNext={handleNext}
                                  onAgeChange={setAudienceAge}
                                  onGenderChange={setAudienceGender}
                                  onAdTextChange={setAdText}
                                  onAdHeadlineChange={setAdHeadline}
                                  isEditMode={isEditMode}
                                  initialAge={draftTargeting.age_min}
                                  initialMaxAge={draftTargeting.age_max}
                                  initialGender={draftTargeting.genders?.[0]}
                                  initialAdText={draftAdText.message}
                                  initialAdHeadline={draftAdText.headlineTop}
                                />
                              </div>

                              {/* Confirmation */}
                              <div
                                ref={(el) =>
                                  (dropdownRefs.current["confirmation"] = el)
                                }
                              >
                                <ConfirmationDropdown
                                  id="confirmation"
                                  expandedId={expandedDropdown}
                                  onToggle={handleDropdownToggle}
                                  isEditMode={isEditMode}
                                  campaignId={campaignId}
                                  draftId={isDraftMode ? draftId : null}
                                  //  onCampaignCreated={handleCampaignCreated}
                                  summary={{
                                    adName,
                                    musicUrl: spotifyUrl,
                                    musicArtist: artistName,
                                    musicTitle: title,
                                    promoteType,
                                    genres: selectedGenres,
                                    audioFileName,
                                    audioFile,
                                    isAudioUploading,
                                    audioUploadProgress,
                                    videos: videos.map((v) => ({
                                      fileName: v.fileName,
                                      isUploading: v.isUploading,
                                      uploadProgress: v.uploadProgress,
                                      file: v.file,
                                      videoUrl: v.videoUrl,
                                    })),
                                    landingFileName,
                                    landingFile,
                                    isLandingUploading,
                                    landingUploadProgress,
                                    country: selectedCountry,
                                    hasSelectedCountry,
                                    interests: selectedInterests,
                                    adAccountName:
                                      metaAdsSelections.adAccountName ??
                                      undefined,
                                    pageName:
                                      metaAdsSelections.pageName ?? undefined,
                                    instagramName:
                                      metaAdsSelections.instagramName ??
                                      undefined,
                                    pixelName:
                                      metaAdsSelections.pixelName ?? undefined,
                                    audienceAge,
                                    audienceGender,
                                    campaignBudget,
                                    currency,
                                    campaignStartDate,
                                    campaignEndDate,
                                    trackImageUrl,
                                    adAccountId:
                                      metaAdsSelections.adAccountId ??
                                      undefined,
                                    pageId:
                                      metaAdsSelections.pageId ?? undefined,
                                    instagramId:
                                      metaAdsSelections.instagramId ??
                                      undefined,
                                    pixelId:
                                      metaAdsSelections.pixelId ?? undefined,
                                    pageImageUrl:
                                      metaAdsSelections.pageImageUrl ?? undefined,
                                    instagramProfileImageUrl:
                                      metaAdsSelections.instagramProfileImageUrl ?? undefined,
                                    isScheduled,
                                    timezone,
                                    startDate,
                                    startTime,
                                    endDate,
                                    endTime,
                                    adText,
                                    adHeadline,
                                  }}
                                  onValidationFailed={handleValidationFailed}
                                />
                              </div>
                            </div>
                          </div>
                        );
                      })()}

                      {/* RIGHT — DESKTOP PREVIEW */}
                      {/* Show preview only when all 4 are filled: music, audio, landing, ad creative */}
                      {(() => {
                        if (!isPreviewReady) return null;

                        return (
                          <>
                            <div
                              ref={previewElementRef}
                              className=" hidden lg:flex w-full lg:w-[450px] xl:w-[500px] order-1 lg:order-2 flex-col items-center mt-6 lg:mt-0 ml-0 lg:ml-[-75px] mr-0 lg:mr-[-120px]
                            flex-shrink-0 self-start will-change-transform"
                              style={{
                                transform: `translateY(${previewOffset}px)`,
                              }}
                            >
                              <MobileAdPreview
                                showPreview
                                spotifyUrl={spotifyUrl}
                                videoFile={
                                  videos.length > 0 ? videos[0].file : null
                                }
                                videos={videos}
                                selectedVideoId={selectedVideoId}
                                onVideoSelect={setSelectedVideoId}
                                audioFile={audioFile}
                                trackImageUrl={trackImageUrl}
                                landingImageFile={landingFile}
                                title={title}
                                artistName={artistName}
                                adText={adText}
                                adHeadline={adHeadline}
                                instagramName={metaAdsSelections.instagramName}
                                pageName={metaAdsSelections.pageName}
                                pageImageUrl={metaAdsSelections.pageImageUrl}
                                instagramProfileImageUrl={metaAdsSelections.instagramProfileImageUrl}
                                pageId={metaAdsSelections.pageId}
                                instagramId={metaAdsSelections.instagramId}
                                isEditOrDraftMode={isEditOrDraftMode}
                              />
                            </div>

                            {isMobilePreviewOpen &&
                              createPortal(
                                <div
                                  className="lg:hidden fixed inset-0 z-[9999] flex items-center justify-center backdrop-blur-md p-4 sm:p-6"
                                  style={{
                                    paddingTop:
                                      "max(0.75rem, env(safe-area-inset-top))",
                                    paddingBottom:
                                      "max(0.75rem, env(safe-area-inset-bottom))",
                                  }}
                                >
                                  <button
                                    type="button"
                                    className="absolute inset-0 bg-black/60"
                                    onClick={() =>
                                      setIsMobilePreviewOpen(false)
                                    }
                                    aria-label="Close preview overlay"
                                  />
                                  <div className="relative w-full max-h-[calc(100vh-2rem)] flex items-center justify-center">
                                    <MobileAdPreview
                                      showPreview
                                      spotifyUrl={spotifyUrl}
                                      videoFile={
                                        videos.length > 0
                                          ? videos[0].file
                                          : null
                                      }
                                      videos={videos}
                                      selectedVideoId={selectedVideoId}
                                      onVideoSelect={setSelectedVideoId}
                                      audioFile={audioFile}
                                      trackImageUrl={trackImageUrl}
                                      landingImageFile={landingFile}
                                      title={title}
                                      artistName={artistName}
                                      adText={adText}
                                      adHeadline={adHeadline}
                                      instagramName={
                                        metaAdsSelections.instagramName
                                      }
                                      pageName={metaAdsSelections.pageName}
                                      pageImageUrl={metaAdsSelections.pageImageUrl}
                                      instagramProfileImageUrl={metaAdsSelections.instagramProfileImageUrl}
                                      pageId={metaAdsSelections.pageId}
                                      instagramId={metaAdsSelections.instagramId}
                                      isEditOrDraftMode={isEditOrDraftMode}
                                      onClose={() => {
                                        setIsMobilePreviewOpen(false);
                                        // Fix: Re-enable body scroll when mobile preview is closed
                                        document.body.style.overflow = "auto";
                                      }}
                                    />
                                  </div>
                                </div>,
                                document.body,
                              )}
                          </>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              </section>
            )}
          </div>
        </div>

        {/* Fixed Preview Screen Button - Mobile, portaled to body so it appears above header; hidden when modal is open */}
        {isPreviewReady &&
          !isMobilePreviewOpen &&
          createPortal(
            <button
              type="button"
              onClick={() => {
                setIsMobilePreviewOpen(true);
                requestAnimationFrame(() => {
                  mobilePreviewRef.current?.scrollIntoView({
                    behavior: "smooth",
                    block: "start",
                  });
                });
                // Fix: Disable body scroll when mobile preview is open
                document.body.style.overflow = "hidden";
              }}
              className="fixed left-4 right-4 md:hidden z-[9999] h-[56px] rounded-full flex items-center justify-center gap-3 text-white text-[14px] font-medium"
              style={{
                background: "linear-gradient(180deg, #4A4F5D 0%, #363C4A 100%)",
                bottom: "max(0.5rem, env(safe-area-inset-bottom))",
              }}
            >
              <img
                src={previewButtonIcon}
                alt="Preview"
                className="w-[28px] h-[28px]"
              />
              <span>Preview Screen</span>
            </button>,
            document.body,
          )}
      </div>
    </SubscriptionProtectedPage>
  );
}
